import type {
  BillingEvent,
  BudgetType,
  CampaignObjective,
  CtaType_Input,
  OptimizationGoal,
} from '../client'

export const DEFAULT_CAMPAIGN_NAME = 'Adlovin Campaign'
export const DEFAULT_ADSET_NAME = 'Adlovin Ad Set'

export type StepStatus = 'pending' | 'in_progress' | 'done' | 'error'

export type ProgressStep = {
  key: string
  label: string
  status: StepStatus
}

export type TargetingLocation = {
  key: string
  name: string
  type: string
  country_code?: string
}

export type AdCreative = {
  adName: string
  primaryText: string
  headline: string
  description: string
  ctaType: CtaType_Input
  ctaLink: string
}

export type PromotionForm = {
  objective: CampaignObjective
  campaignName: string
  existingCampaignId: string
  existingCampaignName: string
  adsetMode: 'new' | 'existing'
  existingAdsetId: string
  existingAdsetName: string
  adsetName: string
  budgetType: BudgetType
  budget: number
  targetingLocations: TargetingLocation[]
  billingEvent: BillingEvent
  optimizationGoal: OptimizationGoal
  startTime: string
  endTime: string
}

export const EMPTY_FORM: PromotionForm = {
  objective: 'OUTCOME_AWARENESS',
  campaignName: DEFAULT_CAMPAIGN_NAME,
  existingCampaignId: '',
  existingCampaignName: '',
  adsetMode: 'existing',
  existingAdsetId: '',
  existingAdsetName: '',
  adsetName: DEFAULT_ADSET_NAME,
  budgetType: 'DAILY',
  budget: 100,
  targetingLocations: [],
  billingEvent: 'IMPRESSIONS',
  optimizationGoal: 'IMPRESSIONS',
  startTime: '',
  endTime: '',
}

export const CTA_LABELS: Record<CtaType_Input, string> = {
  LEARN_MORE: 'Learn More',
  SHOP_NOW: 'Shop Now',
  SIGN_UP: 'Sign Up',
  SUBSCRIBE: 'Subscribe',
  CONTACT_US: 'Contact Us',
  GET_OFFER: 'Get Offer',
  BOOK_TRAVEL: 'Book Travel',
  DOWNLOAD: 'Download',
  ORDER_NOW: 'Order Now',
}

export const OBJECTIVE_GOALS: Record<CampaignObjective, OptimizationGoal[]> = {
  OUTCOME_AWARENESS: ['REACH', 'IMPRESSIONS'],
  OUTCOME_TRAFFIC: ['LINK_CLICKS', 'LANDING_PAGE_VIEWS', 'IMPRESSIONS', 'REACH'],
  OUTCOME_ENGAGEMENT: ['LINK_CLICKS', 'IMPRESSIONS', 'REACH'],
  OUTCOME_SALES: ['LINK_CLICKS', 'LANDING_PAGE_VIEWS', 'IMPRESSIONS', 'REACH'],
}

/** Meta only bills these goals per impression. */
export const GOAL_BILLING: Record<OptimizationGoal, BillingEvent[]> = {
  IMPRESSIONS: ['IMPRESSIONS'],
  REACH: ['IMPRESSIONS'],
  LINK_CLICKS: ['IMPRESSIONS'],
  LANDING_PAGE_VIEWS: ['IMPRESSIONS'],
}

export const OBJECTIVE_GOAL_DEFAULT: Record<CampaignObjective, OptimizationGoal> = {
  OUTCOME_AWARENESS: 'REACH',
  OUTCOME_TRAFFIC: 'LINK_CLICKS',
  OUTCOME_ENGAGEMENT: 'LINK_CLICKS',
  OUTCOME_SALES: 'LINK_CLICKS',
}

export const OPTIMIZATION_GOALS: OptimizationGoal[] = [
  'IMPRESSIONS',
  'REACH',
  'LINK_CLICKS',
  'LANDING_PAGE_VIEWS',
]

export function enumLabel(value: string) {
  return value
    .split('_')
    .map((word) => word[0] + word.slice(1).toLowerCase())
    .join(' ')
}

export function currencySymbol(currency: string) {
  if (!currency) return '$'
  try {
    const parts = new Intl.NumberFormat('en', { style: 'currency', currency }).formatToParts(0)
    return parts.find(({ type }) => type === 'currency')?.value ?? currency
  } catch {
    return currency
  }
}

export function toDatetimeLocal(date: Date) {
  const pad = (value: number) => String(value).padStart(2, '0')
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`
}

export function countryLocation(country: string): TargetingLocation {
  const code = country.toUpperCase()
  return { key: code, name: new Intl.DisplayNames(['en'], { type: 'region' }).of(code) ?? code, type: 'country', country_code: code }
}

export function statusBadgeClass(status?: string | null) {
  switch (status) {
    case 'ACTIVE':
      return 'bg-green-100 text-green-700'
    case 'PAUSED':
      return 'bg-yellow-100 text-yellow-700'
    case 'DELETED':
      return 'bg-red-100 text-red-600'
    default:
      return 'bg-gray-100 text-gray-500'
  }
}
